import { IOnRoute } from '../crud/types';
import type { IWsSaasTenantSearchResult } from 'ws-messages';

/**
 * Represents a SaaS tenant in the system.
 * Used as the data payload when creating/updating a tenant.
 */
export interface INewSaasTenant {
  saas_tenant_id: string; // Unique identifier for the tenant
  disabled?: boolean; // If disabled, the SaaS Tenant account is no longer active
  email: string; // Israeli ID number
  name: string; // Full name of the tenant
  israeli_id?: string;
  address?: string; // Physical address
  phone?: string; // Phone number in Israeli format (05X-XXX-XXXX)
}

/**
 * Represents a SaaS tenant as stored in the Redux state.
 * Extends INewSaasTenant to include on route status and created_at timestamp.
 */
export interface ISaasTenant extends INewSaasTenant, IOnRoute {
  created_at?: string; // ISO string timestamp when the tenant was created
  updated_at?: string; // ISO string timestamp when the tenant was updated
  usage?: IUsageStats; // Usage statistics for the tenant
  credit?: number;
}

/**
 * Represents usage statistics for a SaaS tenant.
 */
export interface IUsageStats {
  total_cost: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_reasoning_tokens: number;
  usage_count: number;
  profiles_used: number;
  first_usage: string;
  last_usage: string;
}

export type ISaasTenantSearchResult = IWsSaasTenantSearchResult;

// SaasTenant CRUD params
export interface ICreateSaasTenantParams extends INewSaasTenant {}

export interface IReadSaasTenantParams {}

export const SET_SAAS_TENANT_CONFIRMED_BY_BACKEND = 'SET_SAAS_TENANT_CONFIRMED_BY_BACKEND';
export interface ISetSaasTenantConfirmedByBackendAction {
  type: typeof SET_SAAS_TENANT_CONFIRMED_BY_BACKEND;
  saas_tenant_id: string;
  updated_at: string;
  saas_tenant_name?: string;
}

// SaasTenant-specific action types
export const SET_SAAS_TENANTS = 'SET_SAAS_TENANTS';
export const ADD_SAAS_TENANT = 'ADD_SAAS_TENANT';
export const UPDATE_SAAS_TENANT = 'UPDATE_SAAS_TENANT';
export const DELETE_SAAS_TENANT = 'DELETE_SAAS_TENANT';
export const CLEAR_SAAS_TENANTS = 'CLEAR_SAAS_TENANTS';
export const UPDATE_SAAS_TENANT_USAGE = 'UPDATE_SAAS_TENANT_USAGE';
export const SET_SAAS_TENANT_SEARCH_RESULTS = 'SET_SAAS_TENANT_SEARCH_RESULTS';
export const SET_SAAS_TENANT_SEARCH_TOO_MANY_RESULTS = 'SET_SAAS_TENANT_SEARCH_TOO_MANY_RESULTS';

// SaasTenant-specific action interfaces
export interface ISetSaasTenantsAction {
  type: typeof SET_SAAS_TENANTS;
  payload: ISaasTenant[];
}

export interface IAddSaasTenantAction {
  type: typeof ADD_SAAS_TENANT;
  payload: ISaasTenant;
}

export interface IUpdateSaasTenantAction {
  type: typeof UPDATE_SAAS_TENANT;
  payload: ISaasTenant;
}

export interface IDeleteSaasTenantAction {
  type: typeof DELETE_SAAS_TENANT;
  payload: {
    saas_tenant_id: string;
  };
}

export interface IClearSaasTenantsAction {
  type: typeof CLEAR_SAAS_TENANTS;
}

export interface IUpdateSaasTenantUsageAction {
  type: typeof UPDATE_SAAS_TENANT_USAGE;
  payload: {
    saas_tenant_id: string;
    usage: {
      prompt_tokens: number;
      completion_tokens: number;
      reasoning_tokens: number;
    };
  };
}

export interface ISetSaasTenantSearchResultsAction {
  type: typeof SET_SAAS_TENANT_SEARCH_RESULTS;
  payload: ISaasTenantSearchResult[];
}

export interface ISetSaasTenantSearchTooManyResultsAction {
  type: typeof SET_SAAS_TENANT_SEARCH_TOO_MANY_RESULTS;
  payload: boolean;
}

export interface ISaasTenantsState {
  saasTenants: ISaasTenant[];
  searchResults: ISaasTenantSearchResult[];
  searchTooManyResults: boolean;
}

export type SaasTenantsActionTypes =
  | ISetSaasTenantsAction
  | IAddSaasTenantAction
  | IUpdateSaasTenantAction
  | IDeleteSaasTenantAction
  | IClearSaasTenantsAction
  | ISetSaasTenantConfirmedByBackendAction
  | ISetSaasTenantSearchResultsAction
  | ISetSaasTenantSearchTooManyResultsAction;
